import React, { useCallback, useEffect, useRef } from 'react'
import { useHistory, useParams, useRouteMatch, Switch, Route, Redirect } from 'react-router-dom'
import { Chip, Grid } from '@material-ui/core'
import AdminPage from '../../admin/shared/AdminPage'
import Loading from '../../../molecules/Loading'
import SydButton from '../../../commonDesign/Button'
import SydTabs from '../../../commonDesign/SydTabs'
import FlexRow from '../../../molecules/FlexRow'
import FlexActions from '../../../molecules/FlexActions'
import {
  useGetManagedSchedule,
  useRemoveManagedSchedule,
  useEnableManagedSchedule,
  useDisableManagedSchedule
} from '../../../../api/scheduler'
import ScheduleConfiguration from './ScheduleConfiguration'
import InvocationHistory from './InvocationHistory'
import ChangeHistory from './ChangeHistory'
import DeleteScheduleDialog from './DeleteScheduleDialog'

const DETAIL_TABS = {
  config: { label: 'Configuration', value: 'config' },
  fires: { label: 'Fire History', value: 'fires' },
  audit: { label: 'Audit Log', value: 'audit' }
}

function ScheduleDetail () {
  const { scheduleId, tab } = useParams()
  const { url, path } = useRouteMatch()
  const history = useHistory()
  const { data: schedule, isLoading } = useGetManagedSchedule(Number(scheduleId))
  const removeSchedule = useRemoveManagedSchedule()
  const enableSchedule = useEnableManagedSchedule()
  const disableSchedule = useDisableManagedSchedule()
  const deleteRef = useRef()

  const basePath = url.replace(/\/(config|fires|audit)$/, '')

  useEffect(() => {
    if (!tab) {
      history.replace(`${basePath}/config`)
    }
  }, [tab, basePath, history])

  const handleTabChange = useCallback((value) => {
    history.replace(`${basePath}/${value}`)
  }, [history, basePath])

  const handleDeleteConfirm = useCallback(async (sched) => {
    await removeSchedule.mutateAsync({ scheduleId: sched.scheduleId })
    history.push(basePath.replace(/\/[^/]+$/, ''))
  }, [removeSchedule, history, basePath])

  const handleToggle = useCallback(() => {
    const mutation = schedule.enabled ? disableSchedule : enableSchedule
    mutation.mutate({ scheduleId: Number(scheduleId) })
  }, [schedule, enableSchedule, disableSchedule, scheduleId])

  if (isLoading || !schedule) {
    return (
      <AdminPage>
        <Loading />
      </AdminPage>
    )
  }

  return (
    <AdminPage>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FlexRow style={{ width: '100%', alignItems: 'center' }}>
            <div>
              <h2 style={{ margin: 0 }}>{schedule.name}</h2>
              <Chip
                label={schedule.enabled ? 'Enabled' : 'Disabled'}
                size='small'
                color={schedule.enabled ? 'primary' : 'default'}
                style={{ marginTop: 4 }}
              />
            </div>
            <FlexActions style={{ flex: 1, gap: '0.5rem' }}>
              <SydButton size='sm' onClick={handleToggle}>
                {schedule.enabled ? 'Disable' : 'Enable'}
              </SydButton>
              <SydButton
                size='sm'
                variant='primary'
                onClick={() => history.push(`${basePath}/edit`)}
              >
                Edit
              </SydButton>
              <SydButton size='sm' variant='ghost' onClick={() => deleteRef.current?.open(schedule)}>
                Delete
              </SydButton>
            </FlexActions>
          </FlexRow>
        </Grid>

        <Grid item xs={12}>
          <SydTabs.Group
            key={tab || 'config'}
            tabOptions={DETAIL_TABS}
            defaultValue={tab || 'config'}
            onChange={handleTabChange}
          >
            <SydTabs.MiniList />
          </SydTabs.Group>
        </Grid>

        <Grid item xs={12}>
          <Switch>
            <Route path={`${path}/config`}>
              <ScheduleConfiguration schedule={schedule} />
            </Route>
            <Route path={`${path}/fires`}>
              <InvocationHistory scheduleId={Number(scheduleId)} />
            </Route>
            <Route path={`${path}/audit`}>
              <ChangeHistory scheduleId={Number(scheduleId)} />
            </Route>
            <Redirect to={`${basePath}/config`} />
          </Switch>
        </Grid>
      </Grid>
      <DeleteScheduleDialog ref={deleteRef} onConfirm={handleDeleteConfirm} />
    </AdminPage>
  )
}

export default ScheduleDetail
